import React, { useState, useEffect, useContext } from 'react';
import CssBaseline from '@material-ui/core/CssBaseline';

import { makeStyles } from "@material-ui/core/styles";
import {useHistory, useParams} from 'react-router-dom';

import InitiativeBackLayout from '../pages/layouts/InitiativeBackLayout';
import DashboardFooter from '../components/DashboardFooter';

import MilestoneEditor from '../components/milestone/MilestoneEditor';
import MilestoneList from '../components/milestone/MilestoneList';
import StakeholderMilestones from "../components/milestone/StakeholderMilestones";

import { ErrorContext, NotificationContext } from "../utils/constants";
import { canEditInitiative } from "../utils/initiatives";
import { getData } from "../utils/apiUtils";
import { meetsMilestoneValidationRequirements } from '../utils/validationUtils';
import Colors from "../utils/colors";

import {getMilestonePlanApprovalBtn} from '../components/initiative/LxApprovalButtons';
import CreateButton from '../components/CreateButton';
import { BodyText, SubsectionHeader } from '../components/Typography';

const useStyles = makeStyles(() => ({
  h1: {
    marginBottom: '5px'
  },
  t1: {
    marginBottom: '1rem'
  },
  commandText: {
    marginTop: '2rem',
    fontSize: '20px'
  },
  main: {
    paddingRight: '10%',
    paddingLeft: '10%',
    paddingTop: '60px',
    width: '100%'
  },
  inputStyle: {
    width: '100%',
    border: '1px solid #ced4da',
    padding: '10px 12px',
    fontSize: '16px',
    transition: 'border-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    borderRadius: '4px',
    backgroundColor: 'white',
    marginBottom: '1.5rem'
  },
  milestoneHelp: {
    marginTop: '2rem',
    padding: '2%',
    width: '34%',
    background: 'rgba(110, 247, 138, 0.1)',
  },
  createButton: {
    marginRight: '1rem'
  },
  approvalButton: {
    margin: '1rem',
    textAlign: 'center'
  },
  approvalBtnDiv: {
    display: 'flex',
    justifyContent: 'space-around',
    paddingTop: '5rem',
    paddingBottom: '5rem',
  },
  approvedCheck: {
    color: Colors.tertiary,
    marginRight: '1rem'
  },
}));

export default function MilestonePlan() {
  const classes = useStyles();
  const params = useParams();
  const history = useHistory();
  const { triggerError } = useContext(ErrorContext);

  // State
  const [ initiativeTeam, setInitiativeTeam ] = useState([]);
  const [ initiative, setInitiative ] = useState({ _links: {} });
  const [ unsavedMilestones, setUnsavedMilestones ] = useState([]);
  const [ milestones, setMilestones ] = useState([]);
  const [ milestoneReviewers, setMilestoneReviewers ] = useState([]);
  const [ temporaryMilestoneKey, setTemporaryMilestoneKey ] = useState(0);
  const [ backUrl, setBackUrl ] = useState(null);
  const notification = useContext(NotificationContext);

  useEffect(() => {
    document.title = 'Rise by TransAlta Initiative Milestones';
    window.scrollTo(0, 0);
  }, []);

  const createMilestone = () => {
    setTemporaryMilestoneKey(temporaryMilestoneKey + 1);
    return {
      key: temporaryMilestoneKey,
      name: '',
      owner: '',
      description: '',
      type: 0,
      startDate: '',
      endDate: '',
    };
  };
  const handleMilestonesError = (error) => {
    if (error.status === 404) {
      history.push('/ui/errors/error404');
    } else {
      onError();
    }
  };
  const onError = () => triggerError({
    title: 'Communication Error',
    description: 'We were unable to read your milestone data from the server. Please try again.',
    onRetry: () => window.location.reload(),
  });

  const fetchInitiative = () => getData('/initiatives/' + params.id, setInitiative, () => {setBackUrl('/')});
  const fetchMilestones = () => getData(`/initiatives/${params.id}/milestones`, setMilestones, handleMilestonesError);

  const refreshData = () => {
      getData('/initiatives/'+ params.id + '/team', setInitiativeTeam, onError);
      getData(`/initiatives/${params.id}/milestones`, setMilestones, handleMilestonesError);
      getData(`/initiatives/milestonereviewers`, setMilestoneReviewers, onError);
      fetchInitiative();
  };

  useEffect(() => {
      refreshData();
  }, [params.id]);

  useEffect(() => {
    if (notification && notification.initiativeId === params.id) {
      refreshData();
    }
  }, [notification]);

  const disabled = () => {
    return !canEditInitiative(initiativeTeam);
  };

  const handleCloseMilestone = (index) => {
    const updatedMilestones = unsavedMilestones.slice(0, index).concat(unsavedMilestones.slice(index+1));
    setUnsavedMilestones(updatedMilestones);
    fetchInitiative();
    getData(`/initiatives/${params.id}/milestones`, setMilestones)
  };

  const getCreateMilestoneBtn = () => {
    if (disabled(initiativeTeam)) {
      return '';
    }

    return (
      <CreateButton onClick={() => { setUnsavedMilestones(Array.prototype.concat(createMilestone(), unsavedMilestones)); }}>
        Create Milestone
      </CreateButton>
    );
  };

  const approvalHeader = () => {
    return ( meetsMilestoneValidationRequirements(milestones) &&
      <>
        <SubsectionHeader>
          Milestone Plan Checkpoint
        </SubsectionHeader>
        <BodyText>
          {initiative._links['MILESTONE_WSL_APPROVAL'] ?
            'When you have reviewed the Milestone Plan, then you can either approve the Plan or send the Plan for Revision.' :
            'When you’ve created a Milestone Plan for your initiative, have it validated before proceeding.'
          }
        </BodyText>
      </>
    );
  };

  // JSX
  return (
    <div className={classes.root} id="body-container">
      <CssBaseline />

      <InitiativeBackLayout hideContentAndFooter={true} backUrl={backUrl}/>

      <main className={classes.main}>
        <h1 className={classes.h1}>Milestone Plan</h1>
        <div className={classes.t1}>
          Planning your initiative, step-by-step. A minimum of three milestones is required. You must have a Delivery Milestone to denote the completion date of your initiative.
        </div>
        <br />
        <MilestoneList
          milestones={milestones}
          initiative={initiative}
          onSuccess={() => { fetchInitiative(); fetchMilestones(); }}
          disabled={disabled()}
        />
        <br />
        {getCreateMilestoneBtn()}
        <br />
        {unsavedMilestones.map((milestone, index) => (
            <MilestoneEditor
                key={`editor-${milestone.key}`}
                initiativeId={params.id}
                milestone={milestone}
                onClose={handleCloseMilestone.bind(null, index)}
                disabled={disabled()}
                milestones={milestones}
            />
        ))}
        <StakeholderMilestones
            milestones={milestones}
            initiative={initiative}
            initiativeTeam={initiativeTeam}
            milestoneReviewers={milestoneReviewers}
            onSuccess={() => { fetchInitiative(); fetchMilestones(); }}
        />
        {approvalHeader()}
        {getMilestonePlanApprovalBtn(meetsMilestoneValidationRequirements(milestones), initiative, fetchInitiative)}
      </main>
      <DashboardFooter />
    </div>
  );
}
